import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, ChevronLeft, ChevronRight, Dna, Download, GitBranch, Globe, LifeBuoy, LogOut, Plus, Shield, StickyNote, UserCircle, Users } from 'lucide-react';
import { apiClient } from '../config/api';
import { ActionSheet, IconButton } from './UI';
import { buildCreateActions, isActive, getPrimaryTargets } from './AppNavigation';
import { canAccessDna, canAccessProspects, useAuth } from '../context/AuthContext';
import { LANGUAGES, getStoredLanguage, setLanguage } from '../utils/language';
import { dogInitial } from '../utils/dogAvatar';
import { onDogsChanged } from '../utils/dogEvents';
import type { SupportedLanguage } from '../i18n';

interface SidebarDog {
  id: number;
  name: string;
  photoUrl: string | null;
}

interface SidebarProps {
  stationName?: string;
  roles: string[];
  isAdmin: boolean;
  /** Ahnentafel nur anzeigen, wenn dafuer ausreichende Abstammungsdaten vorhanden sind. */
  hasPedigreeData: boolean;
  onLogout: () => void;
  /** Nur gesetzt, wenn die App noch nicht installiert ist */
  onInstall?: () => void;
}

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

/**
 * Persistente Navigation ab Tablet-Breite (md:+) - ersetzt dort den bisherigen Kopfzeilen-Header
 * komplett. Auf dem Handy bleiben Header + BottomNav/AppDrawer unveraendert (siehe App.tsx),
 * diese Komponente ist unterhalb von md: per `hidden` unsichtbar.
 */
export const Sidebar: React.FC<SidebarProps> = ({ stationName, roles, isAdmin, hasPedigreeData, onLogout, onInstall }) => {
  const { t } = useTranslation();
  const { isAuthenticated, refreshUser, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const primaryTargets = getPrimaryTargets(t);
  const [dogs, setDogs] = useState<SidebarDog[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [language, setLanguageState] = useState<SupportedLanguage>(getStoredLanguage);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // Kein localStorage verfuegbar (z.B. privater Modus) - Einklappen wirkt dann nur fuer die Sitzung.
    }
  }, [collapsed]);

  useEffect(() => {
    const loadDogs = async () => {
      try {
        const dogsRes = await apiClient.get('/dogs');
        const ownPayload = dogsRes.data?.data ?? dogsRes.data;
        const ownList = Array.isArray(ownPayload) ? ownPayload : [];
        // Nur eigene Hunde ("Deine Hunde") - als "Nicht mein Hund" markierte Hunde bei anderen
        // Haltern gehoeren nicht in die Kurzwahl.
        const ownDogs = ownList.filter((dog: any) => !(dog.hasOtherHolder ?? !!dog.holderName));

        let stationMates: any[] = [];
        try {
          const stationsRes = await apiClient.get('/breeding-stations');
          const activeStation = Array.isArray(stationsRes.data) ? stationsRes.data.find((s: any) => s.isActive) : null;
          if (activeStation) {
            const mateRes = await apiClient.get(`/dogs/station-mates?stationId=${activeStation.id}`);
            const matePayload = mateRes.data?.data ?? mateRes.data;
            stationMates = Array.isArray(matePayload) ? matePayload : [];
          }
        } catch {
          // Stationskameraden bleiben dann einfach leer - nicht kritisch fuer die Navigation.
        }

        const combined = [...ownDogs, ...stationMates]
          .map((dog: any) => ({ id: dog.id, name: dog.name, photoUrl: dog.photoUrl ?? null }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setDogs(combined.slice(0, 8));
      } catch {
        // Kurzwahl bleibt dann einfach leer - nicht kritisch fuer die Navigation.
      }
    };

    loadDogs();
    // Die Sidebar bleibt ueber alle Seiten gemountet - neue Hunde, Namen und Portraits kommen nur so an.
    return onDogsChanged(loadDogs);
  }, []);

  const activeDogMatch = location.pathname.match(/^\/dogs\/(\d+)/);
  const activeDogId = activeDogMatch ? Number(activeDogMatch[1]) : null;

  const avatarClass = (dogId: number) =>
    clsx(
      'shrink-0 overflow-hidden rounded-full border border-dark-500/40',
      collapsed ? 'h-8 w-8' : 'h-10 w-10',
      activeDogId === dogId && 'ring-2 ring-primary-500 ring-offset-2 ring-offset-dark-900'
    );

  const handleLanguageChange = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    setLanguage(lang, isAuthenticated)
      .then(() => {
        // Haelt den in AuthContext/localStorage gecachten Nutzer (inkl. preferredLanguage)
        // synchron, sonst faellt ein spaeterer Reload auf die veraltete Sprache zurueck.
        if (isAuthenticated) return refreshUser();
      })
      .catch(() => {
        // Best-effort: die Auswahl bleibt lokal auch bei fehlgeschlagenem Server-Sync wirksam.
      });
  };

  return (
    <aside
      className={clsx(
        'sidebar hidden md:sticky md:top-0 md:z-10 md:flex md:h-[100dvh] md:shrink-0',
        'md:flex-col md:overflow-y-auto md:overflow-x-hidden md:border-r md:border-primary-400/15',
        'md:bg-dark-900 md:px-3.5 md:py-5 md:transition-[width] md:duration-200',
        collapsed ? 'md:w-[72px]' : 'md:w-[230px]'
      )}
    >
      <div className={clsx('flex items-center gap-2', collapsed ? 'flex-col' : 'justify-between')}>
        <Link to="/dashboard" className="flex items-center gap-2 px-1" title="canebase">
          <img src="/canebase-icon.png" alt="" className="h-8 w-auto shrink-0 object-contain" />
          {!collapsed && <span className="text-gradient text-lg font-bold tracking-tight">canebase</span>}
        </Link>
        <IconButton
          label={collapsed ? t('nav:sidebar.expandNav') : t('nav:sidebar.collapseNav')}
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </IconButton>
      </div>

      {stationName && !collapsed && (
        <span className="station-pill mt-3 inline-flex max-w-full items-center">
          <span className="truncate">{stationName}</span>
        </span>
      )}

      <div className={collapsed ? 'mt-4' : 'mt-6'}>
        {!collapsed && (
          <div className="mb-2 flex items-center justify-between px-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-dark-400">{t('nav:sidebar.yourDogs')}</h2>
            <IconButton label={t('nav:create.title')} onClick={() => setCreateOpen(true)}>
              <Plus size={16} />
            </IconButton>
          </div>
        )}
        <div className={collapsed ? 'flex flex-col items-center gap-2' : 'flex flex-wrap items-start gap-1 rounded-xl p-2'}>
          {dogs.map((dog) =>
            collapsed ? (
              <Link key={dog.id} to={`/dogs/${dog.id}`} title={dog.name} className="flex justify-center rounded-lg p-1 transition-colors hover:bg-dark-700/40">
                <span className={avatarClass(dog.id)}>
                  {dog.photoUrl ? (
                    <img src={dog.photoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="avatar-badge flex h-full w-full items-center justify-center text-xs">{dogInitial(dog.name)}</span>
                  )}
                </span>
              </Link>
            ) : (
              <Link
                key={dog.id}
                to={`/dogs/${dog.id}`}
                title={dog.name}
                className="flex w-14 shrink-0 flex-col items-center gap-1 rounded-lg py-1 text-center transition-colors hover:bg-dark-700/40"
              >
                <span className={avatarClass(dog.id)}>
                  {dog.photoUrl ? (
                    <img src={dog.photoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="avatar-badge flex h-full w-full items-center justify-center text-sm">{dogInitial(dog.name)}</span>
                  )}
                </span>
                <span className="block w-full truncate text-[11px] font-medium leading-tight text-dark-300">{dog.name}</span>
              </Link>
            )
          )}
          <Link
            to="/dogs/new"
            aria-label={t('nav:create.addDog')}
            title={t('nav:create.addDog')}
            className={
              collapsed
                ? 'flex justify-center rounded-lg p-1 transition-colors hover:bg-dark-700/40'
                : 'flex w-14 shrink-0 flex-col items-center gap-1 rounded-lg py-1 text-center transition-colors hover:bg-dark-700/40'
            }
          >
            <span
              className={clsx(
                'flex shrink-0 items-center justify-center rounded-full border-2 border-dashed border-dark-600 text-dark-400 transition-colors',
                collapsed ? 'h-8 w-8' : 'h-10 w-10'
              )}
            >
              <Plus size={16} />
            </span>
            {!collapsed && <span className="block text-[11px] font-medium leading-tight text-dark-300">{t('nav:sidebar.new')}</span>}
          </Link>
        </div>
      </div>

      <nav className="mt-6 flex-1 space-y-1">
        {primaryTargets
          .filter((target) => target.to !== '/account')
          .map((target) => (
            <Link
              key={target.to}
              to={target.to}
              title={target.label}
              className={clsx('drawer-item', collapsed && 'w-full justify-center')}
              aria-current={isActive(location.pathname, location.search, target) ? 'page' : undefined}
            >
              <span className="shrink-0 text-dark-400">{target.icon}</span>
              {!collapsed && <span className="truncate">{target.label}</span>}
            </Link>
          ))}
        <Link
          to="/notizen"
          title={t('nav:drawer.notes')}
          className={clsx('drawer-item', collapsed && 'w-full justify-center')}
          aria-current={location.pathname.startsWith('/notizen') ? 'page' : undefined}
        >
          <span className="shrink-0 text-dark-400">
            <StickyNote size={20} />
          </span>
          {!collapsed && <span className="truncate">{t('nav:drawer.notes')}</span>}
        </Link>
        {canAccessProspects(user) && (
        <Link
          to="/interessenten"
          title={t('nav:drawer.prospects')}
          className={clsx('drawer-item', collapsed && 'w-full justify-center')}
          aria-current={location.pathname.startsWith('/interessenten') ? 'page' : undefined}
        >
          <span className="shrink-0 text-dark-400">
            <Users size={20} />
          </span>
          {!collapsed && <span className="truncate">{t('nav:drawer.prospects')}</span>}
        </Link>
        )}
        {(roles.includes('Zuechter') || isAdmin) && (
          <Link
            to="/uebergaben"
            title={t('transfer:nav.list')}
            className={clsx('drawer-item', collapsed && 'w-full justify-center')}
            aria-current={location.pathname.startsWith('/uebergaben') ? 'page' : undefined}
          >
            <span className="shrink-0 text-dark-400">
              <ArrowRightLeft size={20} />
            </span>
            {!collapsed && <span className="truncate">{t('transfer:nav.list')}</span>}
          </Link>
        )}
        {canAccessDna(user) && (
          <Link
            to="/dna"
            title={t('nav:drawer.dna')}
            className={clsx('drawer-item', collapsed && 'w-full justify-center')}
            aria-current={location.pathname.startsWith('/dna') ? 'page' : undefined}
          >
            <span className="shrink-0 text-dark-400">
              <Dna size={20} />
            </span>
            {!collapsed && <span className="truncate">{t('nav:drawer.dna')}</span>}
          </Link>
        )}
        {hasPedigreeData && (
          <Link
            to="/pedigree"
            title={t('nav:drawer.pedigree')}
            className={clsx('drawer-item', collapsed && 'w-full justify-center')}
            aria-current={location.pathname.startsWith('/pedigree') ? 'page' : undefined}
          >
            <span className="shrink-0 text-dark-400">
              <GitBranch size={20} />
            </span>
            {!collapsed && <span className="truncate">{t('nav:drawer.pedigree')}</span>}
          </Link>
        )}
      </nav>

      <div className="mt-2 space-y-1 border-t border-dark-800/60 pt-2">
        {isAdmin && (
          <Link
            to="/admin"
            title={t('nav:drawer.admin')}
            className={clsx('drawer-item', collapsed && 'w-full justify-center')}
            aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
          >
            <span className="shrink-0 text-dark-400">
              <Shield size={20} />
            </span>
            {!collapsed && <span className="truncate">{t('nav:drawer.admin')}</span>}
          </Link>
        )}
        <Link
          to="/support"
          title={t('nav:drawer.support')}
          className={clsx('drawer-item', collapsed && 'w-full justify-center')}
          aria-current={location.pathname.startsWith('/support') ? 'page' : undefined}
        >
          <span className="shrink-0 text-dark-400">
            <LifeBuoy size={20} />
          </span>
          {!collapsed && <span className="truncate">{t('nav:drawer.support')}</span>}
        </Link>
        <Link
          to="/account"
          title={t('nav:primary.account')}
          className={clsx('drawer-item', collapsed && 'w-full justify-center')}
          aria-current={location.pathname.startsWith('/account') ? 'page' : undefined}
        >
          <span className="shrink-0 text-dark-400">
            <UserCircle size={20} />
          </span>
          {!collapsed && <span className="truncate">{t('nav:primary.account')}</span>}
        </Link>
      </div>

      <div className="mt-2 border-t border-dark-800/60 pt-2">
        <div className={clsx('flex items-center gap-2 px-2 py-1', collapsed && 'flex-col')}>
          <Globe size={16} className="shrink-0 text-dark-400" />
          <select
            aria-label={t('nav:sidebar.language')}
            title={t('nav:sidebar.language')}
            value={language}
            onChange={(e) => handleLanguageChange(e.target.value as SupportedLanguage)}
            className={clsx(
              'rounded-lg border border-dark-700 bg-dark-800 py-1 text-xs text-dark-200',
              collapsed ? 'w-12 px-1 text-center' : 'w-full px-2'
            )}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {collapsed ? lang.nativeLabel : lang.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 space-y-1 border-t border-dark-700 pt-3">
        {onInstall && (
          <button
            type="button"
            title={t('nav:drawer.installApp')}
            className={clsx('drawer-item drawer-item-accent w-full', collapsed && 'justify-center')}
            onClick={onInstall}
          >
            <Download size={20} className="shrink-0" />
            {!collapsed && t('nav:drawer.installApp')}
          </button>
        )}
        <button
          type="button"
          title={t('nav:drawer.logout')}
          className={clsx('drawer-item drawer-item-danger w-full', collapsed && 'justify-center')}
          onClick={onLogout}
        >
          <LogOut size={20} className="shrink-0" />
          {!collapsed && t('nav:drawer.logout')}
        </button>
      </div>

      <ActionSheet
        isOpen={createOpen}
        title={t('nav:create.title')}
        onClose={() => setCreateOpen(false)}
        actions={buildCreateActions(navigate, t, roles.includes('Zuechter') || isAdmin, canAccessProspects(user))}
      />
    </aside>
  );
};
